import { CellClassParams, ExcelStyle, RowClassParams } from '@amzn/gft-ag-grid-enterprise';
import { BorderWeight, DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { datasetSourceFromDrivers, hasDriverMismatch, readPageDrivers } from '@utils/kpiDriverHelpers';
import { useCallback, useMemo } from 'react';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { DecklineRowData } from 'types/Page.types';
import { AnyKPIDrivers } from 'types/PageSidePanel.types';

interface CellClassExtendedCallback {
  (params: CellClassParams<DecklineRowData, string>, defaultColDefStyles: string[]): string[];
}

interface UseDecklineGridStylingProps {
  pageDriverSets: Partial<Record<DatasetSource, AnyKPIDrivers>> | null;
  pageDecklines: EnhancedDeckline[];
  searchMatches: Set<string>;
}

/**
 * Excel Styles for AGGrid Export
 * Ref: https://www.ag-grid.com/react-data-grid/excel-export-styles/
 */
const excelStyles: ExcelStyle[] = [
  {
    id: 'excel-cell-bold',
    font: { bold: true },
  },
  {
    id: 'excel-cell-italics',
    font: { italic: true },
  },
  {
    id: 'excel-cell-underline',
    font: { underline: 'Single' },
  },
  {
    id: 'excel-cell-background-grey',
    interior: {
      color: '#CCCCCC',
      pattern: 'Solid',
    },
    borders: {
      borderBottom: {
        color: '#808080',
        lineStyle: 'Continuous',
        weight: 2,
      },
    },
  },
  {
    id: 'excel-cell-background-transparent',
    interior: {
      pattern: 'None',
    },
  },
  {
    id: 'excel-cell-all-border-outline',
    borders: {
      borderTop: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
      borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
      borderLeft: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
      borderRight: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'excel-cell-bottom-border-outline',
    borders: {
      borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'excel-cell-top-border-outline',
    borders: {
      borderTop: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'excel-cell-left-border-outline',
    borders: {
      borderLeft: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'excel-cell-right-border-outline',
    borders: {
      borderRight: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'excel-cell-thin-line',
    borders: {
      borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 1 },
    },
  },
  {
    id: 'excel-cell-thick-line',
    borders: {
      borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'cell', // Applies to all cells (matches Executive deck expectations)
    font: { size: 30, fontName: 'Arial' },
  },
  {
    id: 'excel-cell-row-number', // Custom override for Row Number column to match Executive Deck output
    font: { size: 23, fontName: 'Arial', bold: true },
    interior: {
      color: '#CCCCCC',
      pattern: 'Solid',
    },
    alignment: {
      horizontal: 'Center',
      vertical: 'Center',
    },
    borders: {
      borderTop: { color: '#FFFFFF', lineStyle: 'Continuous', weight: 2 },
      borderRight: { color: '#FFFFFF', lineStyle: 'Continuous', weight: 2 },
      borderBottom: { color: '#FFFFFF', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'block-header', // Applies to all header groups tagged with the block-header headerClass
    borders: {
      borderTop: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
      borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
      borderLeft: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
      borderRight: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
  },
  {
    id: 'scenario-header', // Applies to all header groups tagged with the scenario-header headerClass
    borders: {
      borderBottom: { color: '#000000', lineStyle: 'Continuous', weight: 2 },
    },
    alignment: {
      horizontal: 'Center',
      vertical: 'Bottom',
      wrapText: true,
    },
  },
  {
    id: 'header', // Applies to scenario period time series headers (should not wrap)
    font: { size: 30, fontName: 'Arial', bold: true },
    alignment: {
      horizontal: 'Center',
      vertical: 'Center',
    },
  },
  {
    id: 'excel-report-granularity', // For custom prepended report granularity
    font: { size: 48, fontName: 'Arial', bold: true },
    alignment: {
      horizontal: 'Center',
      vertical: 'Bottom',
    },
  },
  {
    id: 'excel-report-header', // For custom prepended report header
    font: { size: 40, fontName: 'Arial', bold: true },
    alignment: {
      horizontal: 'Center',
      vertical: 'Center',
    },
  },
  {
    id: 'excel-report-monetary-unit', // For custom prepended monetary unit
    font: { size: 30, fontName: 'Arial', bold: true },
    alignment: {
      horizontal: 'Center',
      vertical: 'Center',
    },
  },
  {
    id: 'excel-footnote-item', // Styles appended footnote row items
    font: { size: 30, fontName: 'Arial' },
    alignment: {
      horizontal: 'Left',
      vertical: 'Top',
    },
  },
  {
    id: 'excel-column-name', // Column letter identifiers (A, B, C...)
    font: { size: 23, fontName: 'Arial', bold: true },
    alignment: {
      horizontal: 'Center',
      vertical: 'Bottom',
    },
    interior: {
      color: '#CCCCCC',
      pattern: 'Solid',
    },
    borders: {
      borderTop: { color: '#FFFFFF', lineStyle: 'Continuous', weight: 2 },
      borderRight: { color: '#FFFFFF', lineStyle: 'Continuous', weight: 2 },
      borderBottom: { color: '#FFFFFF', lineStyle: 'Continuous', weight: 2 },
    },
  },
];

/**
 * Handles AGGrid Cell Styling for Decklines (for Page component)
 * @param props
 */
const useDecklineGridStyling = ({ pageDecklines, searchMatches, pageDriverSets }: UseDecklineGridStylingProps) => {
  /**
   * Utility function to get the displayed format CSS className color for a given Deckline Row, based on the format_cell_color value
   * @param formatCellColor - The format_cell_color value to parse and return the appropriate CSS className color for
   */
  const getFormatCellColor = useCallback((formatCellColor: string) => {
    switch (formatCellColor?.toLowerCase()) {
      case 'grey':
        return 'background-grey';
      case 'white':
        return 'background-transparent'; // Treat 'white' as transparent in view
      default:
        return 'background-transparent';
    }
  }, []);

  /**
   * Memoized Style Lookup for rendering the AGGrid, to aid grid performance
   * Covers non-dynamic styles (e.g. text formatting, border formatting, border weight)
   * and some dynamic styling where possible (e.g. search, new decklines, modified decklines)
   * @returns Map<string, string[]> - A Map of CSS class names to apply to the view
   */
  const styleLookup = useMemo<Map<string, string[]>>(() => {
    const styleMap = new Map<string, string[]>();

    pageDecklines.forEach((deckline) => {
      const classes: string[] = [];

      /** Base Styling */

      // Text formatting
      if (deckline.is_format_bold) classes.push('bold');
      if (deckline.is_format_italics) classes.push('italics');
      if (deckline.is_format_underline) classes.push('underline');
      if (deckline.format_cell_color) classes.push(getFormatCellColor(deckline.format_cell_color));
      // TODO: Decide if we want to respect currentDeckline.format_text_color, and if so apply 'text-white' (or an off-white) or 'text-black'

      // Border formatting
      const { borderbottomflag, bordertopflag, borderleftflag, borderrightflag } = deckline;
      if (borderbottomflag && bordertopflag && borderleftflag && borderrightflag) {
        classes.push('all-border-outline');
      } else {
        if (borderbottomflag) classes.push('bottom-border-outline');
        if (bordertopflag) classes.push('top-border-outline');
        if (borderleftflag) classes.push('left-border-outline');
        if (borderrightflag) classes.push('right-border-outline');
      }

      // Border weight — a modifier of the bottom border, never a border on its own.
      // The thin-line/thick-line SCSS classes only paint a border-bottom, so gate them
      // on borderbottomflag specifically: a stale ("rogue") weight, or a weight on a row
      // whose only border is a non-bottom edge, must not paint a phantom bottom border.
      if (borderbottomflag) {
        if (deckline.borderweightflag === BorderWeight.THIN) classes.push('thin-line');
        else if (deckline.borderweightflag === BorderWeight.THICK) classes.push('thick-line');
      }

      /** Dynamic Styling */

      // For search highlighting, new rows, and modified rows examine the deckline metadata that exists for the row
      let hasSearchMatch = false;
      const internalId = deckline.metadata?.internalId;
      if (internalId && searchMatches.size > 0 && searchMatches.has(internalId)) {
        hasSearchMatch = true;
        classes.push('background-search-highlighted');
      }

      // Marks 'isNewRow' and 'isModifiedRow' items found for highlighting
      if (deckline?.metadata?.isNewRow) {
        classes.push('background-new-row');
      }

      if (deckline?.metadata?.isModified) {
        classes.push('background-modified-row');
      }

      // Background color for cells in a row (when not search highlighted)
      if (deckline.format_cell_color && !hasSearchMatch) classes.push(getFormatCellColor(deckline.format_cell_color));

      // Check for driver mismatch against the page-driver entry for this deckline's own dataset
      if (
        hasDriverMismatch(
          deckline.deckline_drivers,
          readPageDrivers(pageDriverSets, datasetSourceFromDrivers(deckline.deckline_drivers)),
        )
      ) {
        classes.push('background-exception-row');
      }

      // Set the styles to the map
      styleMap.set(deckline.metadata?.internalId, classes);
    });

    return styleMap;
  }, [pageDecklines, getFormatCellColor, searchMatches, pageDriverSets]);

  /**
   * Helper function to get style classes from styleLookup (used in getRowClass and cellClass GridOptions below)
   */
  const getAppliedStyles = useCallback(
    (params: CellClassParams | RowClassParams): string[] => {
      const rowData = params.data as DecklineRowData;
      const internalId = rowData?.rawDecklineData?.metadata?.internalId;
      const styles = styleLookup.get(internalId);

      // Return the entire styles list for use
      return styles || [];
    },
    [styleLookup],
  );

  /**
   * Handles cell level styling for AGGrid, to aid grid performance. Extendable.
   * @param extendedCallback - Optional callback to extend cell level styling
   * @returns CellClassExtendedCallback - A callback function to handle cell level styling
   */
  const cellClassHandler = (extendedCallback?: CellClassExtendedCallback) => {
    return (params: CellClassParams<DecklineRowData>): string[] => {
      // Applies cell level styling as necessary to enable use with the subsequent 'excelStyles' definition
      // Ref: https://www.ag-grid.com/react-data-grid/excel-export-styles/

      // Get existing styles from the precomputed style lookup map
      const currStyles = getAppliedStyles(params);
      // Formatted list of Excel classes to apply that correspond to excelStyles definitions below
      const excelCellStyles: string[] = [];
      // Excel style prefix to apply to current styles to differentiate them for use in exported data
      const excelStylePrefix = 'excel-cell-';

      // Apply row number specific styling only to cells with actual numbers
      if (params.colDef?.field === 'rowNumber' && params.value) {
        excelCellStyles.push('excel-cell-row-number');
      } else {
        // Add Excel-prefixed versions of styles to be applied with excelStyles definitions
        currStyles.forEach((style) => {
          excelCellStyles.push(excelStylePrefix + style);
        });
      }

      return extendedCallback?.(params, excelCellStyles) || excelCellStyles;
    };
  };

  /**
   * Handles row level styling for AGGrid, to aid grid performance
   * @returns string - A string of CSS classes to apply to the row
   */
  const rowClassHandler = (params: RowClassParams<DecklineRowData>) => {
    // Get base styles from the precomputed style lookup map
    const baseStyles = getAppliedStyles(params);
    // Current row-level data
    const rowData = params.data;
    // For row-level dynamic style additions, as needed, that cannot be handled in the memoized styleLookup function
    const dynamicClasses: string[] = [];

    // Subtotal highlighting
    if (rowData?.is_subtotal_highlighted) {
      dynamicClasses.push('background-subtotal-highlighted');
    }

    return [...baseStyles, ...dynamicClasses].join(' ');
  };

  return {
    cellClassHandler,
    getAppliedStyles,
    rowClassHandler,
    styleLookup,
    excelStyles,
  };
};

export default useDecklineGridStyling;
